import { useEffect, useState } from 'react';

import { type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import type { IRowNode } from 'ag-grid-community';

import { ReceiveStatusDetailModal, ReceiveStatusSearchFilter, ReceiveStatusTable } from '~/components/receive';

import { useDownloadReady, useModal } from '~/hooks/common';
import {
  receiveKeys,
  useGetFilterAllQuery,
  useGetReceivingProgressByItemIdMutation,
  useGetReceivingProgressDownloadMutation,
  useGetReceivingProgressQuery,
  useGetReceivingSignSpecificationMutation,
} from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { formatDate, getDateByOffset, isValidDateRange, renderFilterErrorMessage } from '~/utils';

import {
  INITIAL_RECEIVE_PAGE_CURSOR,
  INITIAL_RECEIVE_PAGE_SIZE,
  MAX_RECEIVE_DATE_RANGE,
  OPTION_ALL,
  TOAST_MESSAGE,
} from '~/constants';

import type {
  TypeGetReceivingProgressDownloadParams,
  TypePostStatusRequestedParams,
  TypeReceiveStatusDetailItemInfo,
  TypeReceiveStatusFilter,
  TypeReceiveStatusFilterStatus,
  TypeReceiveStatusInfo,
} from '~/types/receive.types';

const DEFAULT_RECEIVE_STATUS_FILTER: TypeReceiveStatusFilter = {
  keywordType: { value: '', text: '' },
  keyword: '',
  center: OPTION_ALL,
  temperature: OPTION_ALL,
  dateType: { value: '', text: '' },
  dateRange: { start: getDateByOffset(-1, 'day'), end: getDateByOffset(0, 'day') },
  status: OPTION_ALL,
  releaseCode: OPTION_ALL,
  disallowAllYesNo: OPTION_ALL,
  pagination: { currentPage: INITIAL_RECEIVE_PAGE_CURSOR, totalCount: 0, pageSize: INITIAL_RECEIVE_PAGE_SIZE },
};

/**
 * [입고관리 > 입고현황] 기본(FBK / NFA) 콘텐츠
 */
const ReceiveStatusDefaultContent = ({ isFilterOpen }: { isFilterOpen: boolean }) => {
  const queryClient = useQueryClient();

  const receivingProgressDownloadMutation = useGetReceivingProgressDownloadMutation();
  const receivingProgressByItemIdMutation = useGetReceivingProgressByItemIdMutation();
  const receivingSignSpecificationMutation = useGetReceivingSignSpecificationMutation();

  const { data: filterOptions, isError: isFilterOptionsError } = useGetFilterAllQuery('RECEIVING_PROGRESS', {
    showGlobalErrorToast: false,
  });

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const [searchFilterValues, setSearchFilterValues] = useState<TypeReceiveStatusFilter>(DEFAULT_RECEIVE_STATUS_FILTER);
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeReceiveStatusFilterStatus>({
    dateRange: 'default',
  });

  const [listQueryParams, setListQueryParams] = useState<TypePostStatusRequestedParams | null>(null);
  const listQuery = useGetReceivingProgressQuery({
    ownerCode,
    params: listQueryParams ?? ({} as TypePostStatusRequestedParams),
    enabled: !!listQueryParams && !!ownerCode,
  });

  const [selectedItems, setSelectedItems] = useState<TypeReceiveStatusInfo[]>([]);

  const [receiveProgressItemData, setReceiveProgressItemData] = useState<
    (TypeReceiveStatusDetailItemInfo & { receiveRequestId: number }) | null
  >(null);

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: DEFAULT_RECEIVE_STATUS_FILTER,
  });

  const statusDetailModal = useModal();

  useEffect(() => {
    setSearchFilterValues((prev) => ({
      ...prev,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    }));
  }, [filterOptions]);

  useEffect(() => {
    if (isFilterOptionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isFilterOptionsError]);

  useEffect(() => {
    checkFilterChange(searchFilterValues);
  }, [searchFilterValues]);

  useEffect(() => {
    if (listQuery.data && listQueryParams && !listQuery.isPlaceholderData) {
      markDownloadReady({
        filterValue: searchFilterValues,
        hasData: listQuery.data.data.receivingProgresses.length > 0,
      });
    }
  }, [listQuery.data, listQueryParams, listQuery.isPlaceholderData]);

  const handleResetFilter = () => {
    setSearchFilterValues({
      ...DEFAULT_RECEIVE_STATUS_FILTER,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
    });
    setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'default' }));
  };

  const handleSearch = (filter: TypeReceiveStatusFilter = searchFilterValues) => {
    if (!isValidDateRange(filter.dateRange, MAX_RECEIVE_DATE_RANGE)) {
      setSearchFilterValuesStatus((prev) => ({ ...prev, dateRange: 'error' }));

      dialogService.addDialog({ children: `최대 ${MAX_RECEIVE_DATE_RANGE}일 기간으로 입력 후 검색해주세요.` });
      return;
    }

    const { keywordType, keyword, center, temperature, dateType, dateRange, status, releaseCode, disallowAllYesNo } =
      filter;

    const params: TypePostStatusRequestedParams = {
      ...(center?.value && { cluster: center?.value }),
      ...(temperature?.value && { warehouse: temperature?.value }),
      ...(keyword && keywordType && { codeType: keywordType.value }),
      ...(keyword && { code: keyword }),
      dateType: dateType?.value ?? '',
      dateFrom: formatDate(dateRange?.start, 'YYYY-MM-DD') ?? '',
      dateTo: formatDate(dateRange?.end, 'YYYY-MM-DD') ?? '',
      ...(status?.value && { status: status?.value }),
      ...(releaseCode?.value && { releaseCode: releaseCode?.value }),
      ...(disallowAllYesNo?.value && { hasIssue: disallowAllYesNo?.value }),
      limit: filter.pagination.pageSize,
      offset: filter.pagination.currentPage * filter.pagination.pageSize,
    };

    queryClient.invalidateQueries({
      queryKey: receiveKeys.receivingProgress(ownerCode, params),
    });

    setSearchFilterValues(filter);
    setListQueryParams(params);
  };

  const handleClickExcelDownload = () => {
    const { keywordType, keyword, temperature, dateType, dateRange, status, releaseCode, disallowAllYesNo } =
      searchedFilter;

    const params: TypeGetReceivingProgressDownloadParams = {
      ownerCode,
      warehouse: temperature.value,
      codeType: keywordType?.value ?? '',
      code: keyword,
      dateType: dateType?.value ?? '',
      dateFrom: formatDate(dateRange?.start, 'YYYY-MM-DD') ?? '',
      dateTo: formatDate(dateRange?.end, 'YYYY-MM-DD') ?? '',
      status: status.value,
      releaseCode: releaseCode.value,
      hasIssue: disallowAllYesNo.value,
    };

    receivingProgressDownloadMutation.mutate(params, {
      onSuccess: () => {
        toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
      },
    });
  };

  const handleChangeLimit = (option: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        pageSize: option.value,
        currentPage: INITIAL_RECEIVE_PAGE_CURSOR,
      },
    });
  };

  const handleClickPage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: value },
    });
  };

  const handleChangeTableRowSelection = (selectedRows: IRowNode[]) => {
    const selectedItems = selectedRows.map((row) => row.data);

    setSelectedItems(selectedItems);
  };

  const handleViewStatusDetail = (selectedData: TypeReceiveStatusInfo) => {
    receivingProgressByItemIdMutation.mutate(
      { ownerCode, itemId: String(selectedData.requestId) },
      {
        onSuccess: ({ data }) => {
          setReceiveProgressItemData({ ...data, receiveRequestId: selectedData.requestId });
          statusDetailModal.handleOpenModal();
        },
      }
    );
  };

  const handleClickSpecificationDownload = () => {
    receivingSignSpecificationMutation.mutate(
      {
        ownerCode,
        requestNos: selectedItems.map((item) => item.requestCode).join(','),
      },
      {
        onSuccess: () => {
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  return (
    <>
      {isFilterOpen && (
        <ReceiveStatusSearchFilter
          options={filterOptions}
          values={searchFilterValues}
          valuesState={searchFilterValuesStatus}
          onChange={(values) => setSearchFilterValues((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(values) => setSearchFilterValuesStatus((prev) => ({ ...prev, ...values }))}
          onSearch={handleSearch}
          onReset={handleResetFilter}
        />
      )}
      <ReceiveStatusTable
        data={listQuery.data?.data.receivingProgresses ?? null}
        pagination={{
          ...searchFilterValues.pagination,
          totalCount: listQuery.data?.pagination.listTotal ?? 0,
        }}
        isDownloadReady={isDownloadReady}
        selectedItems={selectedItems}
        onChangePageLimit={handleChangeLimit}
        onChangePage={handleClickPage}
        onChangeRowSelection={handleChangeTableRowSelection}
        onClickViewDetail={handleViewStatusDetail}
        onClickReceiveStatusExcelDownload={handleClickExcelDownload}
        onClickSpecificationDownload={handleClickSpecificationDownload}
      />
      {statusDetailModal.isOpen && receiveProgressItemData && (
        <ReceiveStatusDetailModal
          open={statusDetailModal.isOpen}
          data={receiveProgressItemData}
          onConfirm={statusDetailModal.handleCloseModal}
        />
      )}
    </>
  );
};

export default ReceiveStatusDefaultContent;
